import {Model3D} from "./interface";

// 회전 캐시를 위한 Map
const _rotationCache = new Map();

// 박스의 가능한 회전 조합을 반환하는 함수
function getSurfaceRotations([w, l, h]: [number, number, number]): [number, number, number][] {
  const key = [w, l, h].sort((a, b) => a - b).join(",");
  if (_rotationCache.has(key)) return _rotationCache.get(key);

  // 가장 작은 값이 높이가 되고, 나머지 두 값으로 바닥면 결정
  const dims = [w, l, h];
  const sorted = [...dims].sort((a, b) => a - b);
  const height = sorted[0];
  const surface = sorted.slice(1);
  const rots: [number, number, number][] = [
    [surface[0], surface[1], height],
    [surface[1], surface[0], height],
  ];
  _rotationCache.set(key, rots);
  return rots;
}

// Heightmap 기반 3D Packing 클래스
class HeightmapPacker {
  private cellSize: number;
  private width: number;
  private depth: number;
  private heightmap: Uint16Array;
  private lastX: number;
  private lastY: number;
  private _maxZ: number;

  constructor(width: number, depth: number, cellSize = 1) {
    this.cellSize = cellSize;
    this.width = Math.ceil(width / cellSize);
    this.depth = Math.ceil(depth / cellSize);
    this.heightmap = new Uint16Array(this.width * this.depth);
    this.lastX = 0;
    this.lastY = 0;
    this._maxZ = 0;
  }

  // 특정 영역의 최대 높이를 가져오는 함수
  getMaxZ(x: number, y: number, w: number, l: number): number {
    let max = 0;
    for (let yy = y; yy < y + l; yy++) {
      const rowOff = yy * this.width;
      for (let xx = x; xx < x + w; xx++) {
        const z = this.heightmap[rowOff + xx];
        if (z > max) max = z;
      }
    }
    return max;
  }

  // 특정 위치에 박스를 배치할 수 있는지 확인하는 함수
  canPlace(x: number, y: number, w: number, l: number, baseZ: number, spacing: number): boolean {
    for (let yy = y - spacing; yy < y + l + spacing; yy++) {
      if (yy < 0 || yy >= this.depth) continue;
      const rowOff = yy * this.width;
      for (let xx = x - spacing; xx < x + w + spacing; xx++) {
        if (xx < 0 || xx >= this.width) continue;
        if (this.heightmap[rowOff + xx] > baseZ) return false;
      }
    }
    return true;
  }

  // 박스를 배치하고 heightmap을 업데이트하는 함수
  place(x: number, y: number, w: number, l: number, h: number, spacing: number): void {
    const baseZ = this.getMaxZ(x, y, w, l);
    const topZ = baseZ + h + spacing;
    for (let yy = y; yy < y + l; yy++) {
      const rowOff = yy * this.width;
      for (let xx = x; xx < x + w; xx++) {
        this.heightmap[rowOff + xx] = topZ;
      }
    }
    this.lastX = x;
    this.lastY = y;
    if (topZ > this._maxZ) this._maxZ = topZ;
  }

  // 최적의 배치 위치를 찾는 함수
  findBest(
    w: number,
    l: number,
    margin: number,
    spacing: number
  ): {x: number; y: number; baseZ: number} | null {
    const sx = Math.max(0, this.lastX - margin);
    const ex = Math.min(this.width - w, this.lastX + margin);
    const sy = Math.max(0, this.lastY - margin);
    const ey = Math.min(this.depth - l, this.lastY + margin);
    let best = null;

    for (let yy = sy; yy <= ey; yy++) {
      for (let xx = sx; xx <= ex; xx++) {
        const baseZ = this.getMaxZ(xx, yy, w, l);
        if (best && baseZ >= best.baseZ) continue;
        if (!this.canPlace(xx, yy, w, l, baseZ, spacing)) continue;
        best = {x: xx, y: yy, baseZ};
      }
    }
    return best;
  }

  // 현재 최대 높이를 반환하는 함수
  getCurrentHeight(): number {
    return this._maxZ;
  }
}

// PackingItem 타입 서버와 동일하게 추가
export interface PackingItem {
  dimensions: [number, number, number]; // [width, length, height]
  name: string;
  count: number;
}

// 3D Packing 레이아웃을 계산하는 메인 함수
function calculate3DPackingLayout(
  parts: PackingItem[],
  boxDim: [number, number], // [width, depth]
  spacing = 0,
  margin = Infinity,
  cellSize = 5
): {
  finalHeight: number;
  packedParts: Array<{
    position: [number, number, number];
    dims: [number, number, number];
    name: string;
  }>;
} | null {
  const [bedW, bedD] = boxDim;
  const toCells = (v: number) => Math.ceil(v / cellSize);
  const packer = new HeightmapPacker(bedW, bedD, cellSize);

  const items = parts
    .flatMap((p) => Array(p.count).fill({dimensions: p.dimensions, name: p.name}))
    .map(({dimensions, name}) => ({
      dims: dimensions,
      name,
      volume: dimensions[0] * dimensions[1] * dimensions[2],
    }))
    .sort((a, b) => b.volume - a.volume);

  const placed: Array<{
    position: [number, number, number];
    dims: [number, number, number];
    name: string;
  }> = [];

  for (const item of items) {
    const {dims, name} = item;
    const cands: Array<{
      rot: [number, number, number];
      x: number;
      y: number;
      baseZ: number;
      totalZ: number;
      cellDims: [number, number, number];
    }> = [];

    for (const rot of getSurfaceRotations(dims)) {
      const [w, l, h] = rot;
      if (w > bedW || l > bedD) continue;

      const wC = toCells(w);
      const lC = toCells(l);
      const hC = toCells(h);
      const sC = toCells(spacing);
      const mC = toCells(margin);

      const best = packer.findBest(wC, lC, mC, sC);
      if (best) {
        cands.push({
          rot,
          x: best.x,
          y: best.y,
          baseZ: best.baseZ,
          totalZ: best.baseZ + hC,
          cellDims: [wC, lC, hC],
        });
      }
    }

    if (!cands.length) return null;

    cands.sort((a, b) => a.totalZ - b.totalZ);
    const {
      rot,
      x,
      y,
      baseZ,
      cellDims: [wC, lC, hC],
    } = cands[0];

    const [w, l, origH] = rot;
    packer.place(x, y, wC, lC, hC, toCells(spacing));
    placed.push({
      position: [x * cellSize, y * cellSize, baseZ * cellSize],
      dims: [w, l, origH],
      name,
    });
  }

  const finalHeight = packer.getCurrentHeight() * cellSize;

  return {
    finalHeight: 0 < finalHeight ? finalHeight - spacing : 0,
    packedParts: placed,
  };
}

// 새로운 3D bin-packing을 사용하는 함수
export function calculatePart3D(
  models: Model3D[],
  boxWidth: number,
  boxDepth: number,
  spacing: number,
  margin: number,
  cellSize: number
): {
  finalHeight: number;
  packedParts: Array<{
    position: [number, number, number];
    dims: [number, number, number];
    name: string;
  }>;
} | null {
  const parts: PackingItem[] = [];
  for (let m = 0; m < models.length; m++) {
    const model = models[m];
    parts.push({
      dimensions: [model.modelSizeX, model.modelSizeY, model.modelSizeZ], // 서버와 동일하게 [width, length, height]
      name: model.modelName ? model.modelName : `Model_${m}`,
      count: model.modelCount,
    });
  }
  const result = calculate3DPackingLayout(parts, [boxWidth, boxDepth], spacing, margin, cellSize);
  if (!result) return null;

  return {
    finalHeight: result.finalHeight,
    packedParts: result.packedParts.map((part) => ({
      position: part.position,
      dims: part.dims,
      name: part.name,
    })),
  };
}

// PA12 재료비 계산

export function ceiling(number: number, significance: number) {
  if (significance === 0) return 0;
  return Math.ceil(number / significance) * significance;
}

export function volumeToWeight(volume: number, density: number): number {
  return volume * density;
}

export interface MaterialInput {
  weightInGrams?: number;
  volume?: number;
  density?: number;
}

export interface MaterialCostResult {
  roundedWeight: number;
  totalCost: number;
}

export function calculateMaterialCost(
  input: MaterialInput,
  config: undefined | null | {step: number; costPerGram: number}
): MaterialCostResult {
  const {weightInGrams, volume, density = 1} = input;
  let rawWeight: number;
  if (weightInGrams != null) {
    rawWeight = weightInGrams;
  } else if (volume != null) {
    rawWeight = volumeToWeight(volume, density);
  } else {
    throw new Error("weightInGrams 또는 volume 중 하나를 제공해야 합니다.");
  }

  if (!config) {
    config = {
      step: 5,
      costPerGram: 1000,
    };
  }

  const roundedWeight = ceiling(rawWeight / 1000, config.step);
  const totalCost = roundedWeight * config.costPerGram;
  return {roundedWeight, totalCost};
}
